import { useMemo, useState } from "react";
import { getAllCustomers, getCustomerReferrals, getSingleCustomer } from "../api/requests";
import { getAllReferrals } from "../api/requests";
import { ICell } from "../views/shared/customers";
import { capitalizeSentence, getInstallmentTypeFromKey, getPhoneNumberFromShortCodeFormat, getPolicyReviewStatus, getVehicleProtectionFromKey, numberWithCommas, sentenceCase, showToast } from "../utils/miscFunctions";
import { Customer } from "../models/customer/Customer";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ITableFilterItem, ITask } from "../resources/interfaces";
import { ISelectDropdownItem } from "../components/form-control/SelectDropdown";
import { CUSTOMER_TYPE, POLICY_REVIEW_STATES, POLICY_STATUS_OPTIONS } from "../resources/enums";
import { useSelector } from "react-redux";
import { getSelectedCountry } from "../states/slices/resourceSlice";
import { ICustomer } from "../models/customer/ICustomer";
import { GTableActionColumn, ITablePaginationData, ITableRowAction, createSelectSingleColumn } from "../components/elements/GTable/GTable";
import useRoute from "./routesHook";
import PolicyStatus from "../components/elements/PolicyStatus";
import { Ban, CheckCircle, EllipsisVerticalIcon } from "lucide-react";
import moment from "moment";
import PopOnMe from "../components/elements/poponme";
import usePolicy from "./policyHook";
import clsx from "clsx";
import { Policy } from "../models/policy/Policy";
import { getStatus } from "../views/shared/policies/auto/policyDetails";
import { TwoStateVerifiedCheck } from "../components/statuses";
import useUserAccount from "./userAccountHook";
import ReviewPolicy from "../views/shared/policies/auto/ReviewPolicy";
import useModal from "./modalHook";
import { ReactComponent as CancelPolicy } from '../views/shared/customers/imgs/cancel-policy.svg';
import PolicyApproval from "../views/shared/policies/auto/PolicyApproval";
import CreateTask from "../views/admin/leads/CreateTask";
import { useQueryClient } from "@tanstack/react-query";
import Tooltip from "../components/ToolTip";
import EditCustomer from "../views/shared/customers/EditCustomer";
import ActionsPopOnMe from "../components/ActionsPopOnMe";
import RouteLink from "../components/elements/GTable/InGTableComponents/RouteLink";

type IUseCustomerProps = {
  showSingleSelection?: boolean;
}

export default function useCustomer({ showSingleSelection = false }: IUseCustomerProps = {}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') ?? 'all');
  const [customer, setCustomer] = useState<Customer[]>([]);
  const [singleCustomer, setSingleCustomer] = useState<Customer>();
  const [loading, setLoading] = useState<boolean>(true);
  const { getDashboardBase } = useRoute();
  const { PolicyTypesBadge } = usePolicy();
  const { user } = useUserAccount();
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const CustomerModalComponent = ModalComponent;
  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry?.id ?? 1
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [customersPaginationData, setCustomersPaginationData] = useState<ITablePaginationData>();

  const countryCurrency = (id: any) => {
    switch (id) {
      case 1:
        return "GHS";
      case 2:
        return "NGN";
      default:
        break;
    }
  }

  function getLeadStatusFilters() {
    const sources: ISelectDropdownItem[] = [{ name: 'All', value: 'All' }];

    for (const key in POLICY_REVIEW_STATES) {
      if (Object.prototype.hasOwnProperty.call(POLICY_REVIEW_STATES, key)) {
        sources.push({
          name: `${capitalizeSentence(key)} `,
          value: key
        });
      }
    }

    return sources;
  }

  const cancelPolicy = (policy: Policy) => {
    updateModalStates({
      show: true,
      children: (
        <ReviewPolicy
          policy={policy}
          onProceed={() => {
            closeModal();

            const _customer = singleCustomer;
            const policyIndex = _customer?.policyList?.findIndex(
              (_policy: any) => _policy.ref === policy.ref
            );

            if (policyIndex && _customer && _customer.policyList) {
              _customer.policyList[policyIndex].active = false;
              _customer.policyList[policyIndex].reviewStage = POLICY_REVIEW_STATES.CANCELLED;
            }
            setSingleCustomer(_customer);
          }}
          onCancel={closeModal}
          title="Cancel Policy"
          titleIcon={<CancelPolicy />}
          type={POLICY_REVIEW_STATES.CANCELLED}
        />
      ),

      classes: '!p-0 r-[5px]',
      size: 'lg'
    });
  };

  const approvePolicy = (policy: Policy) => {
    updateModalStates({
      show: true,
      children: (
        <PolicyApproval
          policy={policy}
          onApproved={() => {
            closeModal();

            const _customer = singleCustomer;
            const policyIndex = _customer?.policyList?.findIndex(
              (_policy: any) => _policy.ref === policy.ref
            );

            if (policyIndex && _customer && _customer.policyList) {
              _customer.policyList[policyIndex].active = true;
              _customer.policyList[policyIndex].reviewStage = POLICY_REVIEW_STATES.APPROVED;
            }
            setSingleCustomer(_customer);
          }}
          onCancel={closeModal}
        />
      ),

      classes: '!p-0 r-[5px]'
    });
  };

  const showUpdateTaskModal = (task: ITask, selectedoption?: string | ITask) => {
    id && (task || selectedoption) &&
      updateModalStates({
        show: true,
        size: 'xl',
        children: (
          <CreateTask
            onProceed={() => {
              queryClient.invalidateQueries({ queryKey: ['fetchCustomer'] });
              closeModal();
            }}
            onClose={closeModal}
            id={id}
            isEdit={true}
            task={task}
            type={'CUSTOMER'}
            selectedOption={selectedoption}
          />
        ),

        classes: '!p-0 r-[5px]'
      });
  };

  function getDefaultFilters() {
    const filters: ITableFilterItem[] = [

      {
        name: 'stage',
        value: '',
        type: 'select',
        list: getLeadStatusFilters(),
        placeholder: `Select stage`,
        label: `${capitalizeSentence('stage')} `
      },
      {
        name: 'create_date',
        value: '',
        type: 'dateRange',
        placeholder: 'Select start date',
        label: 'Created date'
      }
    ];

    return filters;
  }

  const getTaskTableRowActions = (data: any): ITableRowAction[] => {
    const actions = [
      {
        label: 'Update',
        callback: (task: any) => showUpdateTaskModal(task, data),
        data: data
      }
    ];

    return actions;
  };

  const getTableRowActions = (data: any, type?: string): ITableRowAction[] => {
    const actions = [
      {
        label: 'View',
        callback: (policy: any) => {
          navigate(`${getDashboardBase()}/policies/${policy?.policyNumber}?type=${type}`, { replace: true });
        },
        data: data
      }
    ];

    if (
      !(user.isOwner() || user.isSupervisor()) && data?.reviewStage !== POLICY_REVIEW_STATES.APPROVED) {
      actions.push({
        label: 'Edit',
        callback: (policy: any) => {
          navigate(`${getDashboardBase()}/policies/${policy?.policyNumber}/edit?type=motor`, {
            replace: true
          });
        },
        data: data
      });
    }

    if (user.isOwner() && data?.reviewStage === POLICY_REVIEW_STATES.APPROVED) {
      actions.push({
        label: 'Cancel',
        callback: (policy: Policy) => cancelPolicy(policy),
        data: data
      });
    }

    if (data?.reviewStage === POLICY_REVIEW_STATES.IN_APPROVAL) {
      actions.push({
        label: 'Approve',
        callback: (policy: Policy) => approvePolicy(policy),
        data: data
      });
    }

    return actions;
  };

  const columns = useMemo(
    () => [
      ...(showSingleSelection ? [createSelectSingleColumn()] : []),
      {
        Header: 'Customer Details',
        columns: [
          {
            Header: 'Name',
            accessor: 'firstName',
            Cell: (row: ICell) => {
              const { firstName, lastName } = row.row.original;
              const isCustomerTypeIndividual = row?.row?.original?.customerType === CUSTOMER_TYPE.INDIVIDUAL;
              const isCustomerTypeBusiness = row?.row?.original?.customerType === CUSTOMER_TYPE.BUSINESS;

              return (
                <>
                  <RouteLink to={`${getDashboardBase()}/customers/${row.row.original?.id}`} showTooltip toolTipMessage={`${firstName} ${lastName}`} tooltipDisabled={`${firstName} ${lastName}`.length < 25}>
                    {capitalizeSentence(firstName + ' ' + lastName)}
                  </RouteLink>

                  {isCustomerTypeIndividual ?
                    <Tooltip message="Individual Customer" placement='bottom'>
                      <span className="inline-flex items-center justify-center w-4 h-4 ml-1 text-xs font-bold text-blue-600  rounded-sm border border-blue-600 dark:text-yellow-600 dark:border-yellow-600">
                        i
                      </span>
                    </Tooltip>
                    : isCustomerTypeBusiness ?
                      <Tooltip message="Business Customer" placement='bottom'>
                        <span className="inline-flex items-center justify-center w-4 h-4 ml-1 text-xs font-bold text-blue-600  rounded-sm border border-blue-600 dark:text-yellow-600 dark:border-yellow-600">
                          b
                        </span>
                      </Tooltip>
                      : null
                  }</>

              );
            }
          },
          {
            Header: 'Email',
            accessor: 'email',
            Cell: (row: ICell) => {
              return (
                <span className="">
                  {row.cell.value || '-'}
                </span>
              );
            }
          },
          {
            Header: 'Phone',
            accessor: 'phone',
            Cell: (row: ICell) => {
              return (
                <span className="">
                  {row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Occupation',
            accessor: 'occupation',
            Cell: (row: ICell) => {
              return (
                <span className="">
                  {row.cell.value || '-'}
                </span>
              );
            }
          },
          {
            Header: 'Address',
            accessor: 'digitalAddress',
            Cell: (row: ICell) => {
              return (
                <span className="">
                  {row.cell.value || '-'}
                </span>
              );
            }
          }
        ]
      }
    ],
    [showSingleSelection]
  );

  const mobileColumns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Name',
            accessor: 'customer',
            Cell: (row: ICell) => {
              const { firstName, lastName } = row.row.original;

              return (
                <>
                  <RouteLink to={`${getDashboardBase()}/customers/${row.row.original?.id}`} showTooltip toolTipMessage={`${firstName} ${lastName}`} tooltipDisabled={`${firstName} ${lastName}`.length < 25}>
                    {capitalizeSentence(firstName + ' ' + lastName)}
                  </RouteLink>
                </>
              );
            },
          },

          {
            Header: 'Email',
            accessor: 'email',
            Cell: (row: ICell) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Phone',
            accessor: 'phone',
            Cell: (row: ICell) => {
              return (
                <span>
                  {row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}
                </span>
              );
            }
          }
        ]
      }
    ],
    []
  );

  const autoPoliciesColumns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            id: 'Action',
            Header: 'Action',
            Cell: (row: any) => {
              return (
                <GTableActionColumn actions={getTableRowActions(row.row.original, 'motor')} />
              );
            }
          },
          {
            Header: 'Insurer Policy #',
            accessor: 'insurerPolicyNumber',
            meta: {
              sticky: 'left',
              offset: 40
            },
            Cell: (row: any) => {
              return (
                <RouteLink to={`${getDashboardBase()}/policies/${row?.row?.original.policyNumber}?type=motor`}>
                  {row.cell.value ?? 'Awaiting Policy Number'}
                </RouteLink>
              );
            }
          },
          ...(user?.isInternalAdmin() ? [{
            Header: 'Brolly Policy ID',
            accessor: 'policyNumber',
            Cell: (row: any) => {
              return (
                <Link to={`${getDashboardBase()}/policies/${row.cell.value}?type=motor`}>
                  <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.cell.value ?? '-'}</span>
                </Link>
              );
            }
          }] : []),
          {
            Header: 'Status',
            accessor: 'reviewStage',
            Cell: (row: any) => {
              return (
                <PolicyStatus
                  status={getPolicyReviewStatus(row.cell.value)}
                  firstSuccessfulPayment={row?.original?.isInitialPaymentReceived()}
                  classes="px-2 w-full"
                />
              );
            }
          },
          // {
          //   Header: 'Status',
          //   accessor: 'active',
          //   Cell: (row: any) => {
          //     return (
          //       <div className="">
          //         {row.cell.value ? (
          //           <div className="px-2 border  border-[#0F7125] text-xs flex  h-[24px] w-fit items-center justify-center text-[#0F7125] rounded-full ">
          //             <CheckCircle className="inline-block w-4 h-4 mr-1" />Active
          //           </div>
          //         ) : (
          //           <div className=" px-2 border border-red-800 text-xs flex  h-[24px] w-fit items-center justify-center text-red-800 rounded-full ">
          //             <Ban className="inline-block w-4 h-4 mr-1" /><span>Inactive</span>
          //           </div>
          //         )}
          //       </div>
          //     );
          //   }
          // },
          {
            Header: 'Protection Type',
            accessor: 'protectionType',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? getVehicleProtectionFromKey(row.cell.value)?.title : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Premium',
            accessor: 'outRightPremium',
            Cell: (row: any) => {
              return (
                <div className="min-w-[100px]">
                  <span>GHS {numberWithCommas(row.cell.value)}</span>{' '}
                </div>
              );
            }
          },
          {
            Header: 'Insurer',
            accessor: 'insurerName',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Inception Date',
            accessor: 'inceptionDate',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? moment(row.cell.value).format('DD/MM/YY h:mm a') : '-'}
                </span>
              );
            }
          }
        ]
      }
    ],
    []
  );

  const nonMotorColumns = useMemo(
    () => [
      {
        id: 'selection',
        Header: ({ getToggleAllRowsSelectedProps }: any) => (
          <div className="">
            <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({ row }: any) => (
          <div className="">
            <input type="checkbox" {...row.getToggleRowSelectedProps()} />
          </div>
        )
      },
      {
        Header: 'M',
        columns: [
          {
            Header: "Action",
            id: 'Action',
            Cell: (row: any) => {
              if (getTableRowActions(row.row.original, 'non-motor').length > 0) return (
                <GTableActionColumn actions={getTableRowActions(row.row.original, 'non-motor')} />
              );
            }
          },
          {
            Header: 'Policy ID',
            accessor: 'policyNumber',
            Cell: (row: any) => {
              return (
                <Link
                  to={`${getDashboardBase()}/policies/${row?.cell?.value}?type=non-motor`}>
                  <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.cell.value ?? '-'}</span>
                </Link>
              );
            }
          },

          {
            Header: 'Insurer Policy #',
            accessor: 'insurerPolicyNumber',
            Cell: (row: any) => {
              return (
                <span >{row.cell.value ?? 'Awaiting Policy Number'}</span>
              );
            }
          },
          {
            Header: 'Policy Type',
            accessor: 'policyType',
            Cell: (row: any) => {
              return (
                <PolicyTypesBadge policyType={row.cell.value} />
              );
            }
          },
          {
            Header: 'Status',
            accessor: 'reviewStage',
            Cell: (row: any) => {
              return (
                <PolicyStatus
                  status={getPolicyReviewStatus(row.cell.value)}
                  classes="px-2 w-full"
                />
              );
            }
          },
          {
            Header: 'Currency',
            accessor: 'currency',
            Cell: (row: any) => {
              return (
                <div className="min-w-[100px]">
                  <span>{row.cell.value}</span>
                </div>
              );
            }
          },
          {
            Header: 'Premium',
            accessor: 'grossPremium',
            Cell: (row: any) => {
              return (
                <div className="min-w-[100px]">
                  <span>{numberWithCommas(row.cell.value)}</span>
                </div>
              );
            }
          },
          {
            Header: 'Inception Date',
            accessor: 'inceptionDate',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? moment(row.cell.value).format('DD/MM/YY') : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Insurer',
            accessor: 'insurer.name',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Broker',
            accessor: 'broker.name',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Staff Name',
            accessor: 'staff.fullName',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? row.cell.value : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Created On',
            accessor: 'createdOn',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? moment(row.cell.value).format('DD/MM/YY h:mm a') : '-'}
                </span>
              );
            }
          },
          // {
          //   Header: 'Status',
          //   accessor: 'active',
          //   Cell: (row: any) => {
          //     return (
          //       <div className="w-full flex text-white">
          //         <span
          //           className={clsx(
          //             'w-[30px] h-[30px] rounded-full flex items-center justify-center text-[0px] ',
          //             {
          //               ['bg-status-active']:
          //                 new Policy(row.row.original).getStatus()?.toLowerCase() ===
          //                 POLICY_STATUS_OPTIONS.ACTIVE
          //             },
          //             {
          //               ['bg-status-ended']:
          //                 new Policy(row.row.original).getStatus()?.toLowerCase() ===
          //                 POLICY_STATUS_OPTIONS.INACTIVE
          //             }
          //           )}>
          //           {new Policy(row.row.original).getStatus()}
          //         </span>
          //       </div>
          //     );
          //   }
          // },

        ]
      }
    ],
    [countryId]
  );

  const fleetColumns = useMemo(
    () => [
      {
        id: 'selection',
        Header: ({ getToggleAllRowsSelectedProps }: any) => (
          <div className="">
            <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({ row }: any) => (
          <div className="">
            <input type="checkbox" {...row.getToggleRowSelectedProps()} />
          </div>
        )
      },
      {
        Header: 'M',
        columns: [

          {
            Header: 'Fleet ID',
            accessor: 'fleetNumber',
            Cell: (row: any) => {
              return (
                <Link
                  to={`${getDashboardBase()}/policies/fleets/${row?.row?.original?.fleetNumber}`}
                >
                  <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.cell.value ?? '-'}</span>
                </Link>
              );
            }
          },
          {
            Header: 'Phone #',
            accessor: 'customer.phone',
            Cell: (row: any) => {
              return <span>{getPhoneNumberFromShortCodeFormat(row.cell.value) ?? '-'}</span>;
            }
          },
          {
            Header: 'Status',
            accessor: 'active',
            Cell: (row: any) => {
              return (
                <div className="">
                  {row.cell.value ? (
                    <div className="px-2 border  border-[#0F7125] text-xs flex  h-[24px] w-fit items-center justify-center text-[#0F7125] rounded-full ">
                      <CheckCircle className="inline-block w-4 h-4 mr-1" />Active
                    </div>
                  ) : (
                    <div className=" px-2 border border-red-800 text-xs flex  h-[24px] w-fit items-center justify-center text-red-800 rounded-full ">
                      <Ban className="inline-block w-4 h-4 mr-1" /><span>Inactive</span>
                    </div>
                  )}
                </div>
              );
            }
          },
          {
            Header: 'Total vehicles',
            accessor: 'currentFleetSize',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Contract Status',
            accessor: 'contractStatus.displayName',
            Cell: (row: any) => {
              return (
                <span>{row.cell.value ? row.cell.value : '-'}</span>
              );
            }
          },
          // {
          //   Header: 'Installment Type',
          //   accessor: 'installmentPeriod',
          //   Cell: (row: any) => {
          //     return (
          //       row.cell.value ? <PolicyStatus status={getInstallmentTypeFromKey(row.cell.value)} classes="px-2" /> : <span>-</span>
          //     );
          //   }
          // },
          {
            Header: 'Premium',
            accessor: 'paymentPlan.outrightPremium',
            Cell: (row: any) => {
              return (
                <div className="min-w-[100px]">
                  <span>{countryCurrency(countryId)} {numberWithCommas(row.cell.value)}</span>{' '}
                </div>
              );
            }
          },
          // {
          //   Header: 'Initial Deposit',
          //   accessor: 'paymentPlan.initialDeposit',
          //   Cell: (row: any) => {
          //     return (
          //       <div className="min-w-[100px]">
          //         <span> {countryCurrency(countryId)} {numberWithCommas(row.cell.value)}</span>
          //       </div>
          //     );
          //   }
          // },
          // {
          //   Header: 'Installments',
          //   accessor: 'paymentPlan.installmentPeriod',
          //   Cell: (row: any) => {
          //     return (
          //       <div className="min-w-[100px]">
          //         <span>
          //           {row.cell.value ? capitalizeSentence(row.cell.value) : '-'}
          //         </span>
          //       </div>
          //     );
          //   }
          // },
          // {
          //   Header: 'Total Payment',
          //   accessor: 'paymentPlan.totalPremium',
          //   Cell: (row: any) => {
          //     return (
          //       <div className="min-w-[100px]">
          //         <span>
          //           {' '}
          //           {row.cell.value ? (
          //             <>
          //               <span>{countryCurrency(countryId)}</span> {numberWithCommas(row.cell.value)}
          //             </>
          //           ) : (
          //             '-'
          //           )}
          //         </span>
          //       </div>
          //     );
          //   }
          // },
          {
            Header: 'Inception Date',
            accessor: 'startDate',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? moment(row.cell.value).format('DD/MM/YY h:mm a') : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Insurer',
            accessor: 'insurerinfo.name',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Owner',
            accessor: 'brokerInfo.name',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Manager',
            accessor: 'managerName',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? row.cell.value : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Created On',
            accessor: 'createdAt',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? moment(row.cell.value).format('DD/MM/YY h:mm a') : '-'}
                </span>
              );
            }
          },
        ]
      }
    ],
    [countryId]
  );

  const taskColumns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Name',
            accessor: 'name',
            Cell: (row: any) => {
              return <span>{row.cell.value ? capitalizeSentence(row.cell.value) : '-'}</span>;
            }
          },

          {
            Header: 'Description',
            accessor: 'description',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Type',
            accessor: 'type',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Assigned To',
            accessor: 'assignedTo.firstName',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value
                    ? capitalizeSentence(
                      row.row.original.assignedTo.firstName +
                      ' ' +
                      row.row.original.assignedTo.lastName
                    )
                    : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Status',
            accessor: 'completed',
            Cell: (row: any) => {
              return getStatus(row.cell.value);
            }
          },
          {
            Header: 'Due date',
            accessor: 'dueOn',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? moment(row.cell.value).format('MMM Do YY, h:mm a') : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Action',
            id: 'Action',
            Cell: (row: any) => {
              if (getTaskTableRowActions(row.row.original).length > 0) return (
                <GTableActionColumn actions={getTaskTableRowActions(row.row.original)} />
              );
            }
          }
        ]
      }
    ],
    []
  );

  const referralColumns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Customer Name',
            accessor: 'referrerCustomer',
            Cell: (row: any) => {
              return (
                <>
                  {row?.row?.original?.referrerCustomer ? (
                    <Link to={`${getDashboardBase()}/customers/${row?.row?.original?.referrerCustomer?.id}`} target='blank_' className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">
                      <span>
                        {row?.row?.original?.referrerCustomer?.firstName} {row?.row?.original?.referrerCustomer?.lastName}
                      </span>
                    </Link>
                  ) : (
                    <span className="">-</span>
                  )}
                </>
              );
            }
          },
          {
            Header: 'Referrer',
            accessor: 'referredAccount',
            Cell: (row: any) => {
              return (
                <>
                  {row?.row?.original?.referredAccount ? (
                    <Link to={`${getDashboardBase()}/customers/${row?.row?.original?.referredAccount?.id}`} target='blank_' className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">
                      <span>
                        {row?.row?.original?.referredAccount?.firstName} {row?.row?.original?.referredAccount?.lastName}
                      </span>
                    </Link>
                  ) : (
                    <span className="">-</span>
                  )}
                </>
              );
            }
          },
          {
            Header: 'Code',
            accessor: 'code',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Paid Sale',
            accessor: 'paidSale',
            Cell: (row: any) => {
              return <TwoStateVerifiedCheck verified={row.cell.value} />;
            }
          },
          {
            Header: 'Created On',
            accessor: 'createdOn',
            Cell: (row: any) => {
              return <span>{row.cell.value ? moment(row.cell.value).format('DD-MM-YYYY') : '-'}</span>;
            }
          }
        ]
      }
    ],
    []
  );

  const RNPLColumns = useMemo(() => {
    const baseColumns = [
      {
        Header: 'Applicant',
        accessor: 'customer',
        Cell: ({ row }: any) => (
          <Link
            to={`${getDashboardBase()}/customers/${row.original.customer.id}`}
            target="blank_"
            className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">
            {capitalizeSentence(row.original.customer.firstName)}{' '}
            {capitalizeSentence(row.original.customer.lastName) ?? '-'}
          </Link>
        )
      },
      {
        Header: 'Phone',
        accessor: 'customer.phone',
        Cell: (row: any) => {
          return (
            <span>
              {row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}
            </span>
          );
        }
      },
      {
        Header: 'Application Number',
        accessor: 'applicationNumber',
        Cell: (row: any) => {
          return (
            <Link
              to={`${getDashboardBase()}/rnpl/${row.row?.original?.id}`}
              target="blank_"
              className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">
              {row.cell.value ? row.cell.value : '-'}
            </Link>
          );
        }
      },
      {
        Header: 'Status',
        accessor: 'reviewStage',
        Cell: (row: any) => {
          return (
            <PolicyStatus
              status={
                row.cell.value ? capitalizeSentence(row.cell.value.split('_').join(' ')) : '-'
              }
              classes="rounded-lg px-2  w-full"
            />
          );
        }
      },
      {
        Header: 'Vehicle Number',
        accessor: 'vehicles.registrationNumber',
        Cell: (row: any) => {
          return <span>{row.cell.value ?? '-'}</span>;
        }
      },
      {
        Header: 'Repair Cost',
        accessor: 'repairCost',
        Cell: (row: any) => {
          return <span>{row.cell.value ? row.cell.value : '-'}</span>;
        }
      },
      {
        Header: 'Repair Type',
        accessor: 'repairType',
        Cell: (row: any) => {
          return (
            <span>
              {row.cell.value ? capitalizeSentence(row.cell.value.split('_').join(' ')) : '-'}
            </span>
          );
        }
      },
      {
        Header: 'Created on',
        accessor: 'createdOn',
        Cell: (row: any) => {
          return (
            <span>{row.cell.value ? moment(row.cell.value).format('Do MMM, YYYY') : '-'}</span>
          );
        }
      },
      {
        Header: 'Action',
        id: 'Action',
        Cell: (row: any) => {
          return (
            <GTableActionColumn actions={getTableRowActions(row.row.original)} />
          );
        }
      }
    ];

    return [
      {
        Header: 'M',
        columns: baseColumns
      }
    ];
  }, []);

  const fetchAllReferrals = async () => {
    try {
      const response: any = await getAllReferrals();
      return response ?? [];
    } catch (error) {
      console.error('Error fetching referrals:', error);
      return [];
    }
  };

  const fetchCustomerReferrals = async (customerId: string) => {
    try {
      const response: any = await getCustomerReferrals(customerId);
      return response ?? [];
    } catch (error) {
      console.error('Error fetching referrals:', error);
      return [];
    }
  };

  const getCustomers = async (page = 0, showPerPage: number, countryId: number, stage: string, createTo: string, createFrom: string, customerName: string) => {
    const isElite = activeTab === 'elites' ? true : false;
    try {
      const response = await getAllCustomers(countryId, page, showPerPage, customerName, isElite, stage, createFrom, createTo);

      return response;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomer = async (customerId: string) => {
    try {
      if (customerId) {
        const response: any = await getSingleCustomer(customerId);
        setSingleCustomer(response);
      }
    } catch (error) {
    }
  }

  const showEditCustomerModal = (customer: Customer) => {
    customer &&
      updateModalStates({
        show: true,
        size: '4xl',
        children: (
          <EditCustomer
            customer={customer}
            onProceed={() => {
              queryClient.invalidateQueries({ queryKey: ['fetchCustomer'] });
              closeModal();
            }}
            onClose={closeModal}
          />
        ),
        showCloseButton: true,
        title: 'Edit Customer',
        showTitleSection: true,
        classes: '!p-0 r-[5px]'
      });
  };

  return {
    fetchAllReferrals,
    fetchCustomerReferrals,
    getDefaultFilters,
    getCustomers,
    CustomerModalComponent,
    setSingleCustomer,
    showUpdateTaskModal,
    fetchCustomer,
    showEditCustomerModal,
    columns,
    activeTab,
    mobileColumns,
    customer,
    loading,
    RNPLColumns,
    referralColumns,
    taskColumns,
    fleetColumns,
    nonMotorColumns,
    autoPoliciesColumns,
    customersPaginationData,
    singleCustomer,
  };
}

